package dev.lingvo.site.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.components.AppDocumentTitle
import dev.lingvo.site.components.AuthGuard
import dev.lingvo.site.components.editor.EditorHeaderStyle
import dev.lingvo.site.components.editor.EditorPaneStyle
import dev.lingvo.site.components.editor.KeyPaneStyle
import dev.lingvo.site.components.editor.EDITOR_HEADER_HEIGHT_PX
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LSkeletonShape
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import dev.lingvo.site.i18n.t
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.state.ProjectRouteContext

val EditorLayoutRootStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(100.vh)
        .minHeight(0.px)
        .backgroundColor(colorMode.toSitePalette().surfaceBase)
        .color(colorMode.toSitePalette().textPrimary)
        .styleModifier {
            property("height", "100dvh")
            property("overflow", "hidden")
        }
}

/**
 * The editor's loading shell.
 *
 * It stands in for a workbench that decides its own panes from a measured width, and it has to make
 * the same concession this one cannot measure: a fixed 304px queue beside the editor leaves a phone
 * roughly sixteen pixels of content, under `overflow: hidden`, so the skeleton it draws for the
 * work is invisible on exactly the screens where loading takes longest. Below the width where the
 * queue earns its place, it is one column.
 */
val EditorShellLoadingWorkspaceStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(100.percent)
            .minHeight(0.px)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("overflow", "hidden")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("grid-template-columns", "304px minmax(0, 1fr)")
        }
    }
}

val EditorShellLoadingFrameStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(100.percent)
        .minHeight(0.px)
        .styleModifier {
            property("display", "grid")
            property("grid-template-rows", "52px minmax(0, 1fr)")
            property("position", "relative")
            property("height", "100dvh")
            property("min-height", "100dvh")
            property("max-height", "100dvh")
            property("overflow", "hidden")
        }
}

@Composable
@Layout
fun EditorLayout(
    ctx: PageContext,
    content: @Composable ColumnScope.() -> Unit,
) {
    AuthGuard(loading = { EditorShellLoading() }) {
        val routeSlug = AppRoute.projectSlug(ctx.route.path)
        if (routeSlug == null) {
            ProjectRouteRefusal(
                reason = t("layouts.editor_layout.this_editor_link_is_incomplete", "This editor link is incomplete."),
                onBackToProjects = { ctx.router.tryRoutingTo("/app/projects") },
            )
        } else {
            ProjectRouteContext.Gate(
                routeSlug = routeSlug,
                loading = { EditorShellLoading() },
                refused = { refusal, onRetry ->
                    ProjectRouteRefusal(
                        reason = refusal.reason,
                        retryable = refusal.retryable,
                        onRetry = onRetry,
                        onBackToProjects = { ctx.router.tryRoutingTo("/app/projects") },
                    )
                },
            ) {
                // The project must be verified before even its name enters browser chrome. The
                // editor does not use AppShell, so it owns its document title inside this gate.
                AppDocumentTitle(ctx.route.path.trimEnd('/').ifBlank { "/" })
                Column(EditorLayoutRootStyle.toModifier()) { content() }
            }
        }
    }
}

@Composable
internal fun EditorShellLoading() {
    Div(EditorLayoutRootStyle.toModifier().toAttrs()) {
        Div(EditorShellLoadingFrameStyle.toModifier().toAttrs()) {
            Row(
                EditorHeaderStyle.toModifier(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LSkeleton(
                    width = 72.px,
                    height = 16.px,
                    shape = LSkeletonShape.TEXT,
                    label = t("layouts.editor_layout.loading_editor_header", "Loading editor header"),
                )
                Spacer()
                LSkeleton(
                    width = 104.px,
                    height = 16.px,
                    shape = LSkeletonShape.TEXT,
                    label = t("layouts.editor_layout.loading_editor_progress", "Loading editor progress"),
                )
            }
            Div(EditorShellLoadingWorkspaceStyle.toModifier().toAttrs()) {
                Column(KeyPaneStyle.toModifier().padding(Space.LG).gap(Space.MD)) {
                    LSkeleton(width = 76.percent, height = 18.px, label = t("layouts.editor_layout.loading_key_queue", "Loading key queue"))
                    // Decorative: the bar above already announced the queue, and each `LSkeleton`
                    // is its own `role="status"` — so six repeats were the same sentence spoken
                    // seven times before the editor had said anything.
                    repeat(6) {
                        LSkeleton(height = 48.px, label = null)
                    }
                }
                Column(EditorPaneStyle.toModifier().padding(Space.XXL).gap(Space.LG)) {
                    LSkeleton(width = 44.percent, height = 22.px, label = t("layouts.editor_layout.loading_editor", "Loading editor"))
                    LSkeleton(height = 132.px, label = t("layouts.editor_layout.loading_source", "Loading source"))
                    LSkeleton(height = 232.px, label = t("layouts.editor_layout.loading_translation", "Loading translation"))
                }
            }
        }
    }
}
